import React from "react";
import { cn } from "@/lib/utils";

interface TimelineDotProps {
  /** Visual variant of the dot */
  variant?: "default" | "cluster" | "selected";
  /** Size of the dot */
  size?: "sm" | "md" | "lg";
  /** Additional CSS classes */
  className?: string;
}

/**
 * TimelineDot - Atom component
 *
 * A circular dot displayed on the timeline stem, representing a document or cluster.
 * Uses semantic colors based on variant.
 *
 * @example
 * ```tsx
 * <TimelineDot />
 * <TimelineDot variant="cluster" size="lg" />
 * <TimelineDot variant="selected" />
 * ```
 */
export const TimelineDot = React.memo(function TimelineDot({
  variant = "default",
  size = "md",
  className,
}: TimelineDotProps) {
  return (
    <div
      className={cn(
        // shrink-0 for the same reason as TimelineConnector: a wide card in the
        // same flex row would otherwise shrink the dot away from the stem.
        "shrink-0 rounded-full border-2 border-background shadow-sm transition-all duration-200",
        // Size variants
        size === "sm" && "h-2 w-2",
        size === "md" && "h-3 w-3",
        size === "lg" && "h-4 w-4",
        // Color variants
        variant === "default" && "bg-primary",
        variant === "cluster" && "bg-orange-500",
        variant === "selected" && "bg-blue-600 ring-2 ring-blue-300",
        className
      )}
      aria-hidden="true"
    />
  );
});
